import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Input, List, Popconfirm, Select, Space, Upload, message } from "antd";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { apiService } from "../../services/api";

/**
 * Gestion des sous-titres d'un contenu.
 *
 * Les fichiers WebVTT sont indépendants de la vidéo : ajouter ou retirer une
 * langue ne déclenche aucun retranscodage, contrairement aux pistes intégrées
 * au fichier source.
 */

interface SubtitleTrack {
  language: string;
  label: string;
  folder: string;
  url: string;
}

interface Props {
  contentId: number;
}

// Langues courantes du catalogue. Le champ reste libre pour les autres.
const LANGUAGES = [
  { value: "fra", label: "Français" },
  { value: "eng", label: "English" },
  { value: "ara", label: "العربية" },
  { value: "wol", label: "Wolof" },
  { value: "bam", label: "Bambara" },
  { value: "ful", label: "Pulaar" },
  { value: "spa", label: "Español" },
  { value: "por", label: "Português" },
];

export const SubtitleManager = ({ contentId }: Props) => {
  const [tracks, setTracks] = useState<SubtitleTrack[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [language, setLanguage] = useState<string>("fra");
  const [label, setLabel] = useState<string>("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setTracks(await apiService.listSubtitles(contentId));
    } catch {
      // Vidéo pas encore transcodée : aucune piste, ce n'est pas une erreur
      setTracks([]);
    } finally {
      setLoading(false);
    }
  }, [contentId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleUpload = async () => {
    const file = fileList[0]?.originFileObj;
    if (!file) {
      message.warning("Choisissez un fichier .srt ou .vtt");
      return;
    }
    setUploading(true);
    try {
      await apiService.uploadSubtitle(contentId, file as File, language, label || undefined);
      message.success("Sous-titres ajoutés");
      setFileList([]);
      setLabel("");
      await refresh();
    } catch (error: any) {
      message.error(
        error?.response?.data?.error ?? "Ajout impossible. Vérifiez le format du fichier."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (track: SubtitleTrack) => {
    try {
      await apiService.deleteSubtitle(contentId, track.language);
      message.success(`${track.label} retiré`);
      await refresh();
    } catch {
      message.error("Suppression impossible");
    }
  };

  return (
    <Card title="Sous-titres" size="small">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="La vidéo n'est pas retranscodée"
        description="Les sous-titres sont des fichiers indépendants : ils sont disponibles dans le lecteur dès l'ajout. La vidéo doit déjà être transcodée."
      />

      <Space.Compact style={{ width: "100%", marginBottom: 12 }}>
        <Select
          value={language}
          onChange={setLanguage}
          options={LANGUAGES}
          style={{ width: 160 }}
          showSearch
          placeholder="Langue"
        />
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Libellé affiché (optionnel, ex. « Sous-titres forcés »)"
        />
      </Space.Compact>

      <Space style={{ marginBottom: 16 }}>
        <Upload
          accept=".srt,.vtt"
          maxCount={1}
          fileList={fileList}
          beforeUpload={() => false}
          onChange={({ fileList: list }) => setFileList(list)}
        >
          <Button icon={<UploadOutlined />}>Choisir un fichier .srt ou .vtt</Button>
        </Upload>
        <Button type="primary" onClick={handleUpload} loading={uploading} disabled={!fileList.length}>
          Ajouter
        </Button>
      </Space>

      <List
        size="small"
        loading={loading}
        dataSource={tracks}
        locale={{ emptyText: "Aucun sous-titre pour ce contenu" }}
        renderItem={(track) => (
          <List.Item
            actions={[
              <Popconfirm
                key="delete"
                title="Retirer ces sous-titres ?"
                description="Ils ne seront plus proposés dans le lecteur."
                okText="Retirer"
                cancelText="Annuler"
                onConfirm={() => handleDelete(track)}
              >
                <Button danger size="small" icon={<DeleteOutlined />} />
              </Popconfirm>,
            ]}
          >
            <List.Item.Meta title={track.label} description={track.language} />
          </List.Item>
        )}
      />
    </Card>
  );
};
